import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getRuntimeApiBase } from "./apiBase";

const ADMIN_TOKEN_KEY = "chat_admin_token";
const SESSION_TOKEN_KEY = "chat_session_token";
const THEME_KEY = "chat_theme_v1";
const ACCENT_KEY = "chat_accent_h";

function loadShellTheme() {
  try {
    const raw = localStorage.getItem(THEME_KEY);
    const acc = localStorage.getItem(ACCENT_KEY);
    return {
      dark: raw ? raw === "dark" : true,
      accentHue: acc != null ? Math.min(360, Math.max(0, Number(acc) || 235)) : 235,
    };
  } catch {
    return { dark: true, accentHue: 235 };
  }
}

export default function AdminDashboard() {
  const themeInit = loadShellTheme();
  const [darkMode] = useState(themeInit.dark);
  const [accentHue] = useState(themeInit.accentHue);
  const apiBase = useMemo(() => getRuntimeApiBase(), []);

  const shellStyle = useMemo(
    () => ({
      "--accent-h": String(accentHue),
    }),
    [accentHue]
  );

  const [adminPassword, setAdminPassword] = useState("");
  const [adminToken, setAdminToken] = useState(() => sessionStorage.getItem(ADMIN_TOKEN_KEY) || "");
  const [adminStats, setAdminStats] = useState(null);
  const [adminKickName, setAdminKickName] = useState("");
  const [adminServerAdminName, setAdminServerAdminName] = useState("");
  const [adminBroadcast, setAdminBroadcast] = useState("");
  const [adminBusy, setAdminBusy] = useState(false);
  const [adminError, setAdminError] = useState("");
  const [serverDirectory, setServerDirectory] = useState([]);
  const [serversBusy, setServersBusy] = useState(false);
  const [serversErr, setServersErr] = useState("");
  const [adminTargetGuildId, setAdminTargetGuildId] = useState("");
  const [adminBroadcastGuildId, setAdminBroadcastGuildId] = useState("");
  const [adminGuildMeta, setAdminGuildMeta] = useState(null);
  const [adminGuildMetaTick, setAdminGuildMetaTick] = useState(0);
  const [adminClearChannelSlug, setAdminClearChannelSlug] = useState("");
  const [adminBanName, setAdminBanName] = useState("");
  const [adminServerIconDraft, setAdminServerIconDraft] = useState("");

  useEffect(() => {
    const prev = document.title;
    document.title = "Global admin";
    return () => {
      document.title = prev;
    };
  }, []);

  useEffect(() => {
    if (adminToken) sessionStorage.setItem(ADMIN_TOKEN_KEY, adminToken);
    else sessionStorage.removeItem(ADMIN_TOKEN_KEY);
  }, [adminToken]);

  const authHeaders = useCallback(
    (tokenOverride) => {
      const t = tokenOverride ?? adminToken;
      return t
        ? { Authorization: `Bearer ${t}`, "Content-Type": "application/json" }
        : { "Content-Type": "application/json" };
    },
    [adminToken]
  );

  const refreshServerDirectory = useCallback(
    async (tokenOverride) => {
      const adminTok = tokenOverride ?? adminToken;
      const userTok =
        typeof window !== "undefined" ? localStorage.getItem(SESSION_TOKEN_KEY) || "" : "";
      setServersBusy(true);
      setServersErr("");
      try {
        let list = [];
        if (userTok) {
          const r = await fetch(`${apiBase}/api/servers/mine`, {
            headers: { Authorization: `Bearer ${userTok}` },
          });
          if (r.ok) {
            const data = await r.json();
            list = Array.isArray(data.servers) ? [...data.servers] : [];
          }
        }
        if (adminTok) {
          const ra = await fetch(`${apiBase}/api/admin/servers`, {
            headers: { Authorization: `Bearer ${adminTok}`, "Content-Type": "application/json" },
          });
          if (ra.ok) {
            const ad = await ra.json();
            const extra = Array.isArray(ad.servers) ? ad.servers : [];
            const map = new Map(list.map((s) => [s.id, { ...s }]));
            for (const s of extra) {
              if (!map.has(s.id)) map.set(s.id, { ...s, allServersVisible: true });
            }
            list = [...map.values()];
          }
        }
        list.sort((a, b) => String(a.name || a.id).localeCompare(String(b.name || b.id)));
        setServerDirectory(list);
      } catch (e) {
        setServersErr(e?.message || "Failed to load servers.");
      } finally {
        setServersBusy(false);
      }
    },
    [apiBase, adminToken]
  );

  const refreshAdminStats = useCallback(
    async (tokenOverride) => {
      const t = tokenOverride ?? adminToken;
      if (!t) return;
      setAdminBusy(true);
      setAdminError("");
      try {
        const r = await fetch(`${apiBase}/api/admin/stats`, { headers: authHeaders(t) });
        if (r.status === 403) {
          setAdminToken("");
          setAdminStats(null);
          setAdminError("Session expired. Log in again.");
          return;
        }
        const data = await r.json();
        setAdminStats(data);
      } catch {
        setAdminError("Could not load stats.");
      } finally {
        setAdminBusy(false);
      }
    },
    [adminToken, authHeaders, apiBase]
  );

  useEffect(() => {
    refreshServerDirectory();
  }, [refreshServerDirectory]);

  useEffect(() => {
    if (adminToken) refreshAdminStats();
  }, [adminToken, refreshAdminStats]);

  useEffect(() => {
    if (!adminToken) {
      setAdminGuildMeta(null);
      return;
    }
    const gid = adminTargetGuildId?.trim();
    if (!gid) {
      setAdminGuildMeta(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${apiBase}/api/admin/guild/${encodeURIComponent(gid)}`, {
          headers: authHeaders(),
        });
        const data = await r.json().catch(() => ({}));
        if (cancelled) return;
        if (r.ok) setAdminGuildMeta(data);
        else setAdminGuildMeta(null);
      } catch {
        if (!cancelled) setAdminGuildMeta(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adminToken, adminTargetGuildId, apiBase, authHeaders, adminGuildMetaTick]);

  useEffect(() => {
    const url = adminGuildMeta?.iconUrl != null ? String(adminGuildMeta.iconUrl) : "";
    setAdminServerIconDraft(url);
  }, [adminGuildMeta?.iconUrl, adminTargetGuildId]);

  const adminLogin = async () => {
    setAdminBusy(true);
    setAdminError("");
    try {
      const r = await fetch(`${apiBase}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: adminPassword }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setAdminError(data.error || "Login failed.");
        return;
      }
      const tok = data.token;
      setAdminToken(tok);
      setAdminPassword("");
      await refreshAdminStats(tok);
      await refreshServerDirectory(tok);
    } catch {
      setAdminError("Login failed.");
    } finally {
      setAdminBusy(false);
    }
  };

  const adminLogout = async () => {
    if (!adminToken) {
      setAdminToken("");
      return;
    }
    try {
      await fetch(`${apiBase}/api/admin/logout`, { method: "POST", headers: authHeaders() });
    } catch {
      /* ignore */
    }
    setAdminToken("");
    setAdminStats(null);
  };

  const adminBanUser = async () => {
    const name = adminBanName.trim();
    if (!name) return;
    if (!window.confirm(`Permanently delete account "${name}" from the database? This cannot be undone.`)) return;
    setAdminBusy(true);
    setAdminError("");
    try {
      const r = await fetch(`${apiBase}/api/admin/ban-user`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ userName: name }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) setAdminError(data.error || "Ban failed.");
      else {
        setAdminBanName("");
        await refreshAdminStats();
        await refreshServerDirectory();
      }
    } catch {
      setAdminError("Ban failed.");
    } finally {
      setAdminBusy(false);
    }
  };

  const adminKick = async () => {
    if (!adminKickName.trim()) return;
    setAdminBusy(true);
    setAdminError("");
    try {
      const gid = adminTargetGuildId.trim();
      const r = await fetch(`${apiBase}/api/admin/kick`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          userName: adminKickName.trim(),
          ...(gid ? { guildId: gid } : {}),
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) setAdminError(data.error || "Kick failed.");
      else {
        setAdminKickName("");
        await refreshAdminStats();
      }
    } catch {
      setAdminError("Kick failed.");
    } finally {
      setAdminBusy(false);
    }
  };

  const adminPromoteServerAdmin = async () => {
    const name = adminServerAdminName.trim();
    const gid = adminTargetGuildId.trim();
    if (!name || !gid) return;
    setAdminBusy(true);
    setAdminError("");
    try {
      const r = await fetch(`${apiBase}/api/admin/server-admin/add`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ userName: name, guildId: gid }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) setAdminError(data.error || "Promote failed.");
      else {
        setAdminServerAdminName("");
        await refreshAdminStats();
        setAdminGuildMetaTick((n) => n + 1);
      }
    } catch {
      setAdminError("Promote failed.");
    } finally {
      setAdminBusy(false);
    }
  };

  const adminDemoteServerAdmin = async (name) => {
    if (!name) return;
    const gid = adminTargetGuildId.trim();
    if (!gid) return;
    setAdminBusy(true);
    setAdminError("");
    try {
      const r = await fetch(`${apiBase}/api/admin/server-admin/remove`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ userName: name, guildId: gid }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) setAdminError(data.error || "Remove failed.");
      else {
        await refreshAdminStats();
        setAdminGuildMetaTick((n) => n + 1);
      }
    } catch {
      setAdminError("Remove failed.");
    } finally {
      setAdminBusy(false);
    }
  };

  const adminDoBroadcast = async () => {
    const line = adminBroadcast.trim();
    if (!line) return;
    setAdminBusy(true);
    setAdminError("");
    try {
      const payload = { text: line };
      const bg = adminBroadcastGuildId.trim();
      if (bg) payload.guildId = bg;
      const r = await fetch(`${apiBase}/api/admin/broadcast`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        setAdminError(data.error || "Broadcast failed.");
      } else setAdminBroadcast("");
    } catch {
      setAdminError("Broadcast failed.");
    } finally {
      setAdminBusy(false);
    }
  };

  const adminClearChat = async () => {
    const gid = adminTargetGuildId.trim();
    if (!gid) {
      setAdminError("Choose a target server first.");
      return;
    }
    if (!window.confirm("Clear chat for everyone in this server?")) return;
    setAdminBusy(true);
    setAdminError("");
    try {
      const r = await fetch(`${apiBase}/api/admin/clear-chat`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ guildId: gid }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        setAdminError(data.error || "Clear failed.");
      }
    } catch {
      setAdminError("Clear failed.");
    } finally {
      setAdminBusy(false);
    }
  };

  const adminClearOneChannel = async () => {
    const gid = adminTargetGuildId.trim();
    const ch = adminClearChannelSlug.trim().toLowerCase();
    if (!gid) {
      setAdminError("Choose a target server first.");
      return;
    }
    if (!ch) {
      setAdminError("Enter a channel slug (e.g. general).");
      return;
    }
    if (!window.confirm(`Clear only #${ch} for everyone on this server?`)) return;
    setAdminBusy(true);
    setAdminError("");
    try {
      const r = await fetch(`${apiBase}/api/admin/clear-channel`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ guildId: gid, channel: ch }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        setAdminError(data.error || "Clear channel failed.");
      } else {
        setAdminClearChannelSlug("");
      }
    } catch {
      setAdminError("Clear channel failed.");
    } finally {
      setAdminBusy(false);
    }
  };

  const adminSaveServerIcon = async () => {
    const gid = adminTargetGuildId.trim();
    if (!gid) return;
    setAdminBusy(true);
    setAdminError("");
    try {
      const r = await fetch(`${apiBase}/api/admin/server/icon`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          guildId: gid,
          iconUrl: adminServerIconDraft.trim() === "" ? "" : adminServerIconDraft.trim(),
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) setAdminError(data.error || "Could not update server picture.");
      else {
        await refreshServerDirectory();
        setAdminGuildMetaTick((n) => n + 1);
      }
    } catch {
      setAdminError("Could not update server picture.");
    } finally {
      setAdminBusy(false);
    }
  };

  const adminDeleteServer = async () => {
    const gid = adminTargetGuildId.trim();
    if (!gid) return;
    if (gid === "default") {
      setAdminError("The home server cannot be deleted.");
      return;
    }
    if (!window.confirm(`Delete server ${gid} and all of its data files? Members will lose access.`)) return;
    setAdminBusy(true);
    setAdminError("");
    try {
      const r = await fetch(`${apiBase}/api/admin/server/delete`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ guildId: gid }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) setAdminError(data.error || "Delete failed.");
      else {
        setAdminTargetGuildId("");
        setAdminGuildMeta(null);
        await refreshServerDirectory();
        await refreshAdminStats();
      }
    } catch {
      setAdminError("Delete failed.");
    } finally {
      setAdminBusy(false);
    }
  };

  const adminResetShared = async () => {
    const gid = adminTargetGuildId.trim();
    if (!gid) {
      setAdminError("Choose a target server first.");
      return;
    }
    if (!window.confirm("Reset shared board and poll for everyone in this server?")) return;
    setAdminBusy(true);
    setAdminError("");
    try {
      const r = await fetch(`${apiBase}/api/admin/reset-shared`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ guildId: gid }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        setAdminError(data.error || "Reset failed.");
      }
    } catch {
      setAdminError("Reset failed.");
    } finally {
      setAdminBusy(false);
    }
  };

  return (
    <div
      className={`admin-dashboard app-shell ${darkMode ? "" : "app-theme-light"}`}
      style={{ ...shellStyle, minHeight: "100dvh", display: "flex", flexDirection: "column" }}
    >
      <header className="admin-dash-header">
        <div className="admin-dash-header-inner">
          <Link to="/" className="admin-dash-back">
            <span className="admin-dash-back-icon" aria-hidden>
              ←
            </span>
            Back to chat
          </Link>
          <div className="admin-dash-title-block">
            <h1 className="admin-dash-title">Global admin</h1>
            <p className="admin-dash-subtitle">Manage servers, members, and broadcasts</p>
          </div>
          {adminToken ? (
            <div className="admin-dash-header-actions">
              <button type="button" className="admin-dash-btn admin-dash-btn--ghost" onClick={() => refreshAdminStats()} disabled={adminBusy}>
                Refresh stats
              </button>
              <button type="button" className="admin-dash-btn admin-dash-btn--ghost" onClick={adminLogout}>
                Sign out
              </button>
            </div>
          ) : (
            <span className="admin-dash-badge">Locked</span>
          )}
        </div>
      </header>

      <main className="admin-dash-main">
        {!adminToken ? (
          <section className="admin-dash-card admin-dash-card--login">
            <h2 className="admin-dash-card-title">Unlock dashboard</h2>
            <p className="admin-dash-muted">Enter the global admin password configured on the server.</p>
            <label className="admin-dash-label" htmlFor="admin-pass-dash">
              Password
            </label>
            <input
              id="admin-pass-dash"
              type="password"
              className="admin-dash-input"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && adminLogin()}
              autoComplete="off"
            />
            <button type="button" className="admin-dash-btn admin-dash-btn--primary" onClick={adminLogin} disabled={adminBusy}>
              {adminBusy ? "Checking…" : "Unlock"}
            </button>
          </section>
        ) : (
          <div className="admin-dash-grid">
            <section className="admin-dash-card">
              <h2 className="admin-dash-card-title">Overview</h2>
              <p className="admin-dash-muted">Session lasts up to 24 hours or until you sign out.</p>
              {adminStats ? (
                <div className="admin-dash-stat">
                  <span className="admin-dash-stat-label">Users online</span>
                  <span className="admin-dash-stat-value">{adminStats.online}</span>
                </div>
              ) : (
                <p className="admin-dash-muted">Loading…</p>
              )}
            </section>

            <section className="admin-dash-card admin-dash-card--wide">
              <h2 className="admin-dash-card-title">Target server</h2>
              <p className="admin-dash-muted">
                Used for kicks (membership removal), server admins, clearing chat, and resetting boards. Sign in to the chat app in this
                browser to merge your personal server list with all servers you can administer.
              </p>
              {serversErr ? (
                <p className="admin-dash-alert" role="alert">
                  {serversErr}
                </p>
              ) : null}
              <label className="admin-dash-label" htmlFor="admin-target-guild-dash">
                Server
              </label>
              <select
                id="admin-target-guild-dash"
                className="admin-dash-select"
                value={adminTargetGuildId}
                onChange={(e) => setAdminTargetGuildId(e.target.value)}
                disabled={serversBusy}
              >
                <option value="">Select server…</option>
                {serverDirectory.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name || s.id}
                    {s.allServersVisible ? " · all" : ""}
                  </option>
                ))}
              </select>
            </section>

            <section className="admin-dash-card">
              <h2 className="admin-dash-card-title">Kick user</h2>
              <p className="admin-dash-muted">With a target server selected, removes membership and disconnects.</p>
              <div className="admin-dash-row">
                <input
                  className="admin-dash-input"
                  value={adminKickName}
                  onChange={(e) => setAdminKickName(e.target.value)}
                  placeholder="Exact username"
                />
                <button type="button" className="admin-dash-btn" onClick={adminKick} disabled={adminBusy}>
                  Kick
                </button>
              </div>
            </section>

            <section className="admin-dash-card admin-dash-card--danger-zone">
              <h2 className="admin-dash-card-title">Ban user (delete account)</h2>
              <p className="admin-dash-muted">
                Removes the account from the user database (credentials), clears sessions and economy data, strips all server memberships, and
                disconnects them.
              </p>
              <div className="admin-dash-row">
                <input
                  className="admin-dash-input"
                  value={adminBanName}
                  onChange={(e) => setAdminBanName(e.target.value)}
                  placeholder="Exact username"
                />
                <button type="button" className="admin-dash-btn admin-dash-btn--danger" onClick={adminBanUser} disabled={adminBusy}>
                  Ban and delete account
                </button>
              </div>
            </section>

            <section className="admin-dash-card admin-dash-card--wide">
              <h2 className="admin-dash-card-title">Server picture</h2>
              <p className="admin-dash-muted">HTTPS image URL or an uploaded file path (same rules as profile avatars). Clear the field to remove.</p>
              <label className="admin-dash-label" htmlFor="admin-srv-icon">
                Icon URL
              </label>
              <div className="admin-dash-row">
                <input
                  id="admin-srv-icon"
                  className="admin-dash-input"
                  value={adminServerIconDraft}
                  onChange={(e) => setAdminServerIconDraft(e.target.value)}
                  placeholder="https://…"
                  disabled={!adminTargetGuildId.trim()}
                />
                <button
                  type="button"
                  className="admin-dash-btn admin-dash-btn--primary"
                  onClick={adminSaveServerIcon}
                  disabled={adminBusy || !adminTargetGuildId.trim()}
                >
                  Save
                </button>
              </div>
            </section>

            <section className="admin-dash-card">
              <h2 className="admin-dash-card-title">Server admins</h2>
              {adminGuildMeta?.serverAdmins?.length ? (
                <ul className="admin-dash-list">
                  {adminGuildMeta.serverAdmins.map((n) => (
                    <li key={n} className="admin-dash-list-item">
                      <span>{n}</span>
                      <button
                        type="button"
                        className="admin-dash-btn admin-dash-btn--small admin-dash-btn--danger-text"
                        onClick={() => adminDemoteServerAdmin(n)}
                        disabled={adminBusy}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="admin-dash-muted">
                  {adminTargetGuildId ? "No server admins listed, or still loading." : "Pick a server above."}
                </p>
              )}
              <div className="admin-dash-row">
                <input
                  className="admin-dash-input"
                  value={adminServerAdminName}
                  onChange={(e) => setAdminServerAdminName(e.target.value)}
                  placeholder="Username to promote"
                />
                <button
                  type="button"
                  className="admin-dash-btn admin-dash-btn--primary"
                  onClick={adminPromoteServerAdmin}
                  disabled={adminBusy || !adminTargetGuildId.trim()}
                >
                  Promote
                </button>
              </div>
            </section>

            <section className="admin-dash-card admin-dash-card--wide">
              <h2 className="admin-dash-card-title">Broadcast</h2>
              <label className="admin-dash-label" htmlFor="admin-bc-scope-dash">
                Scope
              </label>
              <select
                id="admin-bc-scope-dash"
                className="admin-dash-select"
                value={adminBroadcastGuildId}
                onChange={(e) => setAdminBroadcastGuildId(e.target.value)}
              >
                <option value="">All servers</option>
                {serverDirectory.map((s) => (
                  <option key={`bc-${s.id}`} value={s.id}>
                    {s.name || s.id}
                  </option>
                ))}
              </select>
              <textarea
                className="admin-dash-textarea"
                value={adminBroadcast}
                onChange={(e) => setAdminBroadcast(e.target.value)}
                rows={3}
                placeholder="Message shown as a system line…"
              />
              <button type="button" className="admin-dash-btn admin-dash-btn--primary" onClick={adminDoBroadcast} disabled={adminBusy}>
                Send broadcast
              </button>
            </section>

            <section className="admin-dash-card admin-dash-card--wide admin-dash-card--danger-zone">
              <h2 className="admin-dash-card-title">Danger zone</h2>
              <p className="admin-dash-muted">These actions affect everyone in the selected server.</p>
              <div className="admin-dash-actions-stack">
                <button
                  type="button"
                  className="admin-dash-btn admin-dash-btn--danger"
                  onClick={adminClearChat}
                  disabled={adminBusy || !adminTargetGuildId.trim()}
                >
                  Clear all channels (this server)
                </button>
                <div className="admin-dash-row">
                  <input
                    className="admin-dash-input"
                    value={adminClearChannelSlug}
                    onChange={(e) => setAdminClearChannelSlug(e.target.value)}
                    placeholder="channel slug"
                  />
                  <button
                    type="button"
                    className="admin-dash-btn admin-dash-btn--danger"
                    onClick={adminClearOneChannel}
                    disabled={adminBusy || !adminTargetGuildId.trim()}
                  >
                    Clear one channel
                  </button>
                </div>
                <button
                  type="button"
                  className="admin-dash-btn admin-dash-btn--danger"
                  onClick={adminResetShared}
                  disabled={adminBusy || !adminTargetGuildId.trim()}
                >
                  Reset shared boards & polls
                </button>
                <button
                  type="button"
                  className="admin-dash-btn admin-dash-btn--danger"
                  onClick={adminDeleteServer}
                  disabled={adminBusy || !adminTargetGuildId.trim() || adminTargetGuildId.trim() === "default"}
                >
                  Delete entire server
                </button>
              </div>
            </section>
          </div>
        )}

        {adminError ? (
          <div className="admin-dash-toast admin-dash-toast--error" role="alert">
            {adminError}
          </div>
        ) : null}
      </main>
    </div>
  );
}
